import { useState, useEffect } from 'react';
import { 
  getUseCases, 
  createTaxonomy, 
  updateTaxonomy,
  naturalLanguageEditTaxonomy,
  fallbackToUC001Taxonomy,
  validateTaxonomyStructure
} from '../services/api';

const TaxonomyEditor = ({ taxonomyData, onSave, onCancel }) => {
  const [useCases, setUseCases] = useState([]);
  const [selectedUseCaseIds, setSelectedUseCaseIds] = useState([]);
  const [formData, setFormData] = useState({
    usecase_id: '',
    taxonomy: [],
    partner_priority: [],
    max_offers: 2,
    max_bonus_tips: 3,
    bonus_tip_text: "Wir haben Ihnen ein paar passende Ratgeberartikel zusammengestellt – vielleicht ist etwas Hilfreiches für Sie und Ihre Familie dabei:"
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jsonEditor, setJsonEditor] = useState("");
  const [naturalLanguagePrompt, setNaturalLanguagePrompt] = useState("");
  const [isProcessingNaturalLanguage, setIsProcessingNaturalLanguage] = useState(false);
  const [jsonValidationError, setJsonValidationError] = useState("");
  const [showAdvancedMode, setShowAdvancedMode] = useState(false);

  // Available field options based on your data structure
  const fieldOptions = [
    'patientDement',
    'mobilitaet', 
    'pflegegrad',
    'hilfsmittel',
    'treppenVorhanden',
    'krankheiten',
    'hoeherBeantragt'
  ];

  // Predefined category options - each can only be used once
  const categoryOptions = [
    'Demenz',
    'Mobil', 
    'Pflegegrad',
    'Inkontinenz'
  ];

  // Available condition types
  const conditionTypes = [
    { value: 'equals', label: 'Gleich' },
    { value: 'in', label: 'Enthalten in' },
    { value: 'not_in', label: 'Nicht enthalten in' },
    { value: 'exists', label: 'Existiert' }
  ];

  // Mobility options for dropdowns
  const mobilityOptions = [
    'leichtGehbehindert',
    'rollator',
    'rollstuhlMitEigenerKraft', 
    'rollstuhl',
    'bettlaegerig'
  ];

  const krankheitenOptions = [
    'inkontinenz',
    'demenz',
    'diabetes',
    'weitere'
  ];

  const hilfsmittelOptions = [
    'treppenlift',
    'pflegebett',
    'rollator',
    'rollstuhl'
  ];

  const pflegegradOptions = [
    'eins',
    'zwei',
    'drei',
    'vier',
    'fuenf'
  ];

  // Predefined cooperation partners
  const cooperationPartners = [
    'Treppenlift von Sonilift',
    'PflegeWelt',
    'PflegeABC',
    'Sanera',
    'E-Scooter & E-Rollstuhl von Fellerhoff',
    'HerzBegleiter'
  ];

  const loadUseCases = async () => {
    try {
      const data = await getUseCases();
      if (Array.isArray(data)) {
        setUseCases(data);
      }
    } catch (error) {
      console.error('Error loading use cases:', error);
    }
  };

  // Add this function definition
  const getAvailableUseCases = () => {
    // Return all use cases except UC001
    return useCases.filter(uc => uc.id !== 'UC001');
  };

  const handleUseCaseChange = (usecaseId, isSelected) => {
    const newSelectedIds = isSelected 
      ? [...selectedUseCaseIds, usecaseId]
      : selectedUseCaseIds.filter(id => id !== usecaseId);
    
    setSelectedUseCaseIds(newSelectedIds);
    setFormData(prev => ({
      ...prev,
      usecase_ids: newSelectedIds
    }));
  };

  useEffect(() => {
    loadUseCases();
    if (taxonomyData) {
      // Handle both old and new format
      const usecase_ids = taxonomyData.usecase_ids || 
                         (taxonomyData.usecase_id ? [taxonomyData.usecase_id] : []);
      setSelectedUseCaseIds(usecase_ids.filter(id => id)); // Remove empty strings
      
      setFormData({
        usecase_ids: usecase_ids,
        taxonomy: taxonomyData.taxonomy || [],
        partner_priority: taxonomyData.partner_priority || [],
        max_offers: taxonomyData.max_offers || 2,
        max_bonus_tips: taxonomyData.max_bonus_tips || 3,
        bonus_tip_text: taxonomyData.bonus_tip_text || "Wir haben Ihnen ein paar passende Ratgeberartikel zusammengestellt – vielleicht ist etwas Hilfreiches für Sie und Ihre Familie dabei:"
      });
    } else {
      setSelectedUseCaseIds([]);
      setFormData({
        usecase_ids: [],
        taxonomy: [],
        partner_priority: [],
        max_offers: 2,
        max_bonus_tips: 3,
        bonus_tip_text: ""
      });
    }
  }, [taxonomyData]);

  // Get all available categories (static + dynamic from current taxonomy)
  const getAllAvailableCategories = () => {
    const staticCategories = [
      'Demenz',
      'Mobil', 
      'Pflegegrad',
      'Inkontinenz'
    ];
    
    // Add categories from current taxonomy
    const dynamicCategories = formData.taxonomy?.map(cat => cat.category) || [];
    
    // Combine and remove duplicates
    return [...new Set([...staticCategories, ...dynamicCategories])];
  };

  // Get available categories (not already used)
  const getAvailableCategories = () => {
    const usedCategories = formData.taxonomy.map(cat => cat.category);
    return getAllAvailableCategories().filter(cat => !usedCategories.includes(cat));
  };

  const addCategory = () => {
    setFormData(prev => ({
      ...prev,
      taxonomy: [...prev.taxonomy, {
        category: '',
        rules: []
      }]
    }));
  };

  const updateCategory = (categoryIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.map((cat, idx) => 
        idx === categoryIndex ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const removeCategory = (categoryIndex) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.filter((_, idx) => idx !== categoryIndex)
    }));
  };

  const addRule = (categoryIndex) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.map((cat, idx) => 
        idx === categoryIndex ? {
          ...cat,
          rules: [...cat.rules, {
            conditions: [],
            content: {
              "cooperation-partner": "",
              "link": "",
              "text": "",
              "bonustip": ""
            }
          }]
        } : cat
      )
    }));
  };

  const removeRule = (categoryIndex, ruleIndex) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.map((cat, idx) => 
        idx === categoryIndex ? {
          ...cat,
          rules: cat.rules.filter((_, rIdx) => rIdx !== ruleIndex)
        } : cat
      )
    }));
  };

  const addCondition = (categoryIndex, ruleIndex) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.map((cat, catIdx) => 
        catIdx === categoryIndex ? {
          ...cat,
          rules: cat.rules.map((rule, rIdx) => 
            rIdx === ruleIndex ? {
              ...rule,
              conditions: [...rule.conditions, {
                field: '',
                type: 'equals',
                value: ''
              }]
            } : rule
          )
        } : cat
      )
    }));
  };

  const updateCondition = (categoryIndex, ruleIndex, conditionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.map((cat, catIdx) => 
        catIdx === categoryIndex ? {
          ...cat,
          rules: cat.rules.map((rule, rIdx) => 
            rIdx === ruleIndex ? {
              ...rule,
              conditions: rule.conditions.map((cond, cIdx) => 
                cIdx === conditionIndex ? { ...cond, [field]: value } : cond
              )
            } : rule
          )
        } : cat
      )
    }));
  };

  const removeCondition = (categoryIndex, ruleIndex, conditionIndex) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.map((cat, catIdx) => 
        catIdx === categoryIndex ? {
          ...cat,
          rules: cat.rules.map((rule, rIdx) => 
            rIdx === ruleIndex ? {
              ...rule,
              conditions: rule.conditions.filter((_, cIdx) => cIdx !== conditionIndex)
            } : rule
          )
        } : cat
      )
    }));
  };

  const updateContent = (categoryIndex, ruleIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      taxonomy: prev.taxonomy.map((cat, catIdx) => 
        catIdx === categoryIndex ? {
          ...cat,
          rules: cat.rules.map((rule, rIdx) => 
            rIdx === ruleIndex ? {
              ...rule,
              content: { ...rule.content, [field]: value }
            } : rule
          )
        } : cat
      )
    }));
  };

  const addPartnerPriority = () => {
    setFormData(prev => ({
      ...prev,
      partner_priority: [...prev.partner_priority, {
        "cooperation-partner": "",
        "priority": prev.partner_priority.length + 1
      }]
    }));
  };

  const updatePartnerPriority = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      partner_priority: prev.partner_priority.map((partner, idx) => 
        idx === index ? { ...partner, [field]: value } : partner
      )
    }));
  };

  const removePartnerPriority = (index) => {
    setFormData(prev => ({
      ...prev,
      partner_priority: prev.partner_priority.filter((_, idx) => idx !== index)
    }));
  };

  const getValueInput = (condition, categoryIndex, ruleIndex, conditionIndex) => {
    if (condition.type === 'exists') {
      return null; // No value needed for exists
    }

    if (condition.type === 'equals' && condition.field === 'patientDement') {
      return (
        <select
          value={condition.value}
          onChange={(e) => updateCondition(categoryIndex, ruleIndex, conditionIndex, 'value', e.target.value === 'true')}
          style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="">Wählen...</option>
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </select>
      );
    }

    if (condition.type === 'equals' && condition.field === 'treppenVorhanden') {
      return (
        <select
          value={condition.value}
          onChange={(e) => updateCondition(categoryIndex, ruleIndex, conditionIndex, 'value', e.target.value === 'true')}
          style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="">Wählen...</option>
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </select>
      );
    }

    if (condition.type === 'equals' && condition.field === 'hoeherBeantragt') {
      return (
        <select
          value={condition.value}
          onChange={(e) => updateCondition(categoryIndex, ruleIndex, conditionIndex, 'value', e.target.value)}
          style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="">Wählen...</option>
          <option value="NOT">Nicht beantragt</option>
          <option value="YES">Beantragt</option>
        </select>
      );
    }

    if ((condition.type === 'in' || condition.type === 'not_in')) {
      let options = [];
      if (condition.field === 'mobilitaet') options = mobilityOptions;
      else if (condition.field === 'krankheiten') options = krankheitenOptions;
      else if (condition.field === 'hilfsmittel') options = hilfsmittelOptions;
      else if (condition.field === 'pflegegrad') options = pflegegradOptions;

      return (
        <div>
          <div style={{ marginBottom: '4px', fontSize: '12px', color: '#666' }}>
            Mehrfachauswahl (Ctrl+Click):
          </div>
          <select
            multiple
            value={Array.isArray(condition.value) ? condition.value : []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => option.value);
              updateCondition(categoryIndex, ruleIndex, conditionIndex, 'value', values);
            }}
            style={{ 
              padding: '4px', 
              borderRadius: '4px', 
              border: '1px solid #ccc',
              minHeight: '80px',
              width: '100%'
            }}
          >
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <input
        type="text"
        value={condition.value || ''}
        onChange={(e) => updateCondition(categoryIndex, ruleIndex, conditionIndex, 'value', e.target.value)}
        style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}
        placeholder="Wert eingeben..."
      />
    );
  };

  const handleSave = async () => {
    if (!selectedUseCaseIds.length) {
      setError('Bitte mindestens einen UseCase auswählen.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const dataToSave = {
        ...formData,
        usecase_ids: selectedUseCaseIds
      };

      if (taxonomyData?._id || taxonomyData?.usecase_id) {
        // Use _id if available, otherwise use usecase_id
        const id = taxonomyData._id || taxonomyData.usecase_id;
        await updateTaxonomy(id, dataToSave);
      } else {
        await createTaxonomy(dataToSave);
      }
      onSave();
    } catch (error) {
      setError('Fehler beim Speichern: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const isProtectedTaxonomy = taxonomyData?.usecase_id === 'UC001';

  const validateAndUpdateJson = (jsonText) => {
    try {
      const parsed = JSON.parse(jsonText);
      
      // Basic structure validation
      if (!parsed.taxonomy || !Array.isArray(parsed.taxonomy)) {
        throw new Error("Taxonomy must be an array");
      }

      // Validate each category
      for (const category of parsed.taxonomy) {
        if (!category.category || typeof category.category !== 'string') {
          throw new Error("Each category must have a valid 'category' field");
        }
        
        if (!category.rules || !Array.isArray(category.rules)) {
          throw new Error("Each category must have a 'rules' array");
        }

        // Validate each rule
        for (const rule of category.rules) {
          if (!rule.conditions || !Array.isArray(rule.conditions)) {
            throw new Error("Each rule must have a 'conditions' array");
          }

          if (!rule.content || typeof rule.content !== 'object') {
            throw new Error("Each rule must have a 'content' object");
          }

          // Validate conditions
          for (const condition of rule.conditions) {
            if (!condition.field || !condition.type) {
              throw new Error("Each condition must have 'field' and 'type'");
            }
          }
        }
      }

      // If validation passes, update the form data
      setFormData(prev => ({
        ...prev,
        taxonomy: parsed.taxonomy,
        partner_priority: parsed.partner_priority || prev.partner_priority,
        max_offers: parsed.max_offers || prev.max_offers,
        max_bonus_tips: parsed.max_bonus_tips || prev.max_bonus_tips,
        bonus_tip_text: parsed.bonus_tip_text || prev.bonus_tip_text
      }));

      setJsonValidationError("");
      return true;
    } catch (error) {
      setJsonValidationError(`JSON Validation Error: ${error.message}`);
      return false;
    }
  };

  const handleJsonEditorChange = (value) => {
    setJsonEditor(value);
    setJsonValidationError("");
  };

  const handleJsonEditorSave = () => {
    if (validateAndUpdateJson(jsonEditor)) {
      setShowAdvancedMode(false);
    }
  };

  const handleNaturalLanguageEdit = async () => {
    if (!naturalLanguagePrompt.trim()) {
      setJsonValidationError("Please enter a natural language instruction");
      return;
    }

    setIsProcessingNaturalLanguage(true);
    setJsonValidationError("");

    try {
      const result = await naturalLanguageEditTaxonomy(formData, naturalLanguagePrompt, selectedUseCaseIds);
      
      if (result.success) {
        const newFormData = {
          ...formData,
          taxonomy: result.data.taxonomy,
          partner_priority: result.data.partner_priority || formData.partner_priority,
          max_offers: result.data.max_offers || formData.max_offers,
          max_bonus_tips: result.data.max_bonus_tips || formData.max_bonus_tips,
          bonus_tip_text: result.data.bonus_tip_text || formData.bonus_tip_text
        };
        
        setFormData(newFormData);
        
        // Update JSON editor if it's open
        if (showAdvancedMode) {
          setJsonEditor(JSON.stringify(newFormData, null, 2));
        }
        
        setNaturalLanguagePrompt("");
        setJsonValidationError("");
        
        // Show success message
        setTimeout(() => {
          setJsonValidationError("✅ Changes applied successfully using AI!");
          setTimeout(() => setJsonValidationError(""), 3000);
        }, 100);
      } else {
        setJsonValidationError(result.error || "Failed to process natural language instruction");
      }
    } catch (error) {
      setJsonValidationError(`Error: ${error.message}`);
    } finally {
      setIsProcessingNaturalLanguage(false);
    }
  };

  const handleFallbackToUC001 = async () => {
    if (!window.confirm("This will replace the current taxonomy with UC001's immutable content. This action cannot be undone. Continue?")) {
      return;
    }

    setLoading(true);
    try {
      const result = await fallbackToUC001Taxonomy(selectedUseCaseIds);
      
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          taxonomy: result.data.taxonomy,
          partner_priority: result.data.partner_priority,
          max_offers: result.data.max_offers,
          max_bonus_tips: result.data.max_bonus_tips,
          bonus_tip_text: result.data.bonus_tip_text
        }));
        setJsonValidationError("");
      } else {
        setJsonValidationError(result.error || "Failed to load UC001 taxonomy");
      }
    } catch (error) {
      setJsonValidationError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update JSON editor when formData changes
  useEffect(() => {
    if (showAdvancedMode) {
      setJsonEditor(JSON.stringify(formData, null, 2));
    }
  }, [formData, showAdvancedMode]);

  // Get all available partners (static + dynamic from current taxonomy)
  const getAllAvailablePartners = () => {
    const staticPartners = [
      'Treppenlift von Sonilift',
      'PflegeWelt',
      'PflegeABC',
      'Sanera',
      'E-Scooter & E-Rollstuhl von Fellerhoff',
      'HerzBegleiter'
    ];
    
    // Add partners from current taxonomy
    const dynamicPartners = formData.partner_priority?.map(p => p["cooperation-partner"]) || [];
    
    // Combine and remove duplicates
    return [...new Set([...staticPartners, ...dynamicPartners])];
  };

  // Get all available fields (static + dynamic from current taxonomy)
  const getAllAvailableFields = () => {
    const staticFields = [
      'patientDement',
      'mobilitaet', 
      'pflegegrad',
      'hilfsmittel',
      'treppenVorhanden',
      'krankheiten',
      'hoeherBeantragt'
    ];
    
    // Add fields from current taxonomy
    const dynamicFields = [];
    formData.taxonomy?.forEach(category => {
      category.rules?.forEach(rule => {
        rule.conditions?.forEach(condition => {
          if (condition.field && !staticFields.includes(condition.field)) {
            dynamicFields.push(condition.field);
          }
        });
      });
    });
    
    // Combine and remove duplicates
    return [...new Set([...staticFields, ...dynamicFields])];
  };

  // Get all available condition types (static + dynamic)
  const getAllAvailableConditionTypes = () => {
    const staticTypes = [
      { value: 'equals', label: 'Gleich' },
      { value: 'in', label: 'Enthalten in' },
      { value: 'not_in', label: 'Nicht enthalten in' },
      { value: 'exists', label: 'Existiert' }
    ];
    
    // Add types from current taxonomy
    const dynamicTypes = [];
    formData.taxonomy?.forEach(category => {
      category.rules?.forEach(rule => {
        rule.conditions?.forEach(condition => {
          if (condition.type && !staticTypes.find(t => t.value === condition.type)) {
            dynamicTypes.push({ value: condition.type, label: condition.type });
          }
        });
      });
    });
    
    // Combine and remove duplicates
    return [...staticTypes, ...dynamicTypes];
  };

  const addCategoryWithRule = () => {
    const newCategory = {
      category: '',
      rules: [{
        conditions: [{
          field: '',
          type: 'equals',
          value: ''
        }],
        content: {
          "cooperation-partner": "",
          "link": "",
          "text": "",
          "bonustip": ""
        }
      }]
    };
    
    setFormData(prev => ({
      ...prev,
      taxonomy: [...prev.taxonomy, newCategory]
    }));
  };

  const createQuickCategory = async (categoryName, fieldName, fieldValue) => {
    const instruction = `add a new category called ${categoryName} with a rule of ${fieldName} equals ${fieldValue}`;
    
    setIsProcessingNaturalLanguage(true);
    setJsonValidationError("");

    try {
      const result = await naturalLanguageEditTaxonomy(formData, instruction, selectedUseCaseIds);
      
      if (result.success) {
        const newFormData = {
          ...formData,
          taxonomy: result.data.taxonomy,
          partner_priority: result.data.partner_priority || formData.partner_priority,
          max_offers: result.data.max_offers || formData.max_offers,
          max_bonus_tips: result.data.max_bonus_tips || formData.max_bonus_tips,
          bonus_tip_text: result.data.bonus_tip_text || formData.bonus_tip_text
        };
        
        setFormData(newFormData);
        
        // Update JSON editor if it's open
        if (showAdvancedMode) {
          setJsonEditor(JSON.stringify(newFormData, null, 2));
        }
        
        setJsonValidationError("✅ Category created successfully!");
        setTimeout(() => setJsonValidationError(""), 3000);
      } else {
        setJsonValidationError(result.error || "Failed to create category");
      }
    } catch (error) {
      setJsonValidationError(`Error: ${error.message}`);
    } finally {
      setIsProcessingNaturalLanguage(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxHeight: '80vh', overflowY: 'auto' }}>
      <h2 style={{ color: 'var(--primary-coral)', marginBottom: '24px' }}>
        {taxonomyData?._id ? 'Taxonomie bearbeiten' : 'Neue Taxonomie erstellen'}
      </h2>

      {/* Protection Warning for UC001 */}
      {isProtectedTaxonomy && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          color: '#856404',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div>
            <strong>Geschützte Taxonomie:</strong> UC001 ist eine Systemtaxonomie und kann nicht bearbeitet werden.
          </div>
        </div>
      )}

      {/* UseCase Selection */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
          UseCase ID: <span style={{ color: 'red' }}>*</span>
        </label>
        <div style={{
          border: '1px solid var(--medium-gray)',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: 'var(--light-gray)'
        }}>
          {getAvailableUseCases().length === 0 ? (
            <p style={{ color: 'var(--text-light)', fontStyle: 'italic' }}>
              Keine UseCases verfügbar
            </p>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
              gap: '12px'
            }}>
              {getAvailableUseCases().map(useCase => (
                <label 
                  key={useCase.id} 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: selectedUseCaseIds.includes(useCase.id) 
                      ? 'var(--secondary-pink)' 
                      : 'white',
                    border: selectedUseCaseIds.includes(useCase.id)
                      ? '2px solid var(--primary-coral)'
                      : '1px solid var(--medium-gray)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedUseCaseIds.includes(useCase.id)}
                    onChange={(e) => handleUseCaseChange(useCase.id, e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>
                      {useCase.id}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      {useCase.name}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          
          {selectedUseCaseIds.length > 0 && (
            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              backgroundColor: 'var(--secondary-pink-light)',
              borderRadius: '6px'
            }}>
              <strong>Ausgewählte UseCases ({selectedUseCaseIds.length}):</strong>
              <div style={{ marginTop: '8px' }}>
                {selectedUseCaseIds.map(id => (
                  <span 
                    key={id}
                    style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      margin: '2px',
                      backgroundColor: 'var(--primary-coral)',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    {id}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global Settings */}
      <div style={{ 
        opacity: isProtectedTaxonomy ? 0.6 : 1,
        pointerEvents: isProtectedTaxonomy ? 'none' : 'auto'
      }}>
        <div style={{ 
          border: '2px solid var(--accent-coral)', 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '24px',
          backgroundColor: 'var(--secondary-pink-light)'
        }}>
          <h3 style={{ marginBottom: '16px', color: 'var(--primary-coral)' }}>⚙️ Globale Einstellungen</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Max. Angebote:</label>
              <input
                type="number"
                value={formData.max_offers}
                onChange={(e) => setFormData(prev => ({ ...prev, max_offers: parseInt(e.target.value) || 0 }))}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}
                min="0"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Max. Bonus-Tipps:</label>
              <input
                type="number"
                value={formData.max_bonus_tips}
                onChange={(e) => setFormData(prev => ({ ...prev, max_bonus_tips: parseInt(e.target.value) || 0 }))}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}
                min="0"
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Bonus-Tipp Text:</label>
            <textarea
              value={formData.bonus_tip_text}
              onChange={(e) => setFormData(prev => ({ ...prev, bonus_tip_text: e.target.value }))}
              style={{ 
                width: '100%', 
                height: '60px', 
                padding: '8px', 
                borderRadius: '4px', 
                border: '1px solid #ccc',
                resize: 'vertical'
              }}
              placeholder="Text für Bonus-Tipps..."
            />
          </div>

          {/* Partner Priorities */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontWeight: '600' }}>Partner-Prioritäten:</label>
              <button
                onClick={addPartnerPriority}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'var(--primary-coral)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                + Partner
              </button>
            </div>
            
            {formData.partner_priority.map((partner, index) => (
              <div key={index} style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr auto auto', 
                gap: '8px', 
                marginBottom: '8px',
                alignItems: 'center'
              }}>
                <select
                  value={partner["cooperation-partner"]}
                  onChange={(e) => updatePartnerPriority(index, "cooperation-partner", e.target.value)}
                  style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">Partner wählen...</option>
                  {getAllAvailablePartners().map(cp => (
                    <option key={cp} value={cp}>{cp}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={partner.priority}
                  onChange={(e) => updatePartnerPriority(index, "priority", parseInt(e.target.value) || 1)}
                  style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '60px' }}
                  min="1"
                />
                <button
                  onClick={() => removePartnerPriority(index)}
                  style={{
                    backgroundColor: '#ff6b6b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Kategorien</h3>

          {getAvailableCategories().length === 0 && formData.taxonomy.length > 0 && (
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              padding: '8px 12px', 
              borderRadius: '4px', 
              marginBottom: '16px',
              fontSize: '14px',
              color: '#1976d2'
            }}>
              ℹ️ Alle verfügbaren Kategorien wurden bereits hinzugefügt.
            </div>
          )}

          {formData.taxonomy.map((category, categoryIndex) => (
            <div
              key={categoryIndex}
              style={{
                border: '2px solid var(--secondary-pink)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                backgroundColor: 'var(--white)'
              }}
            >
              {/* Category Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <select
                  value={category.category}
                  onChange={(e) => updateCategory(categoryIndex, 'category', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '16px',
                    fontWeight: '600',
                    width: '200px'
                  }}
                >
                  <option value="">Kategorie wählen...</option>
                  {category.category && !getAvailableCategories().includes(category.category) && (
                    <option value={category.category}>{category.category}</option>
                  )}
                  {getAvailableCategories().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeCategory(categoryIndex)}
                  style={{
                    backgroundColor: 'var(--primary-coral)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  🗑️ Kategorie löschen
                </button>
              </div>

              {/* Rules */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4>Regeln</h4>
                  <button
                    onClick={() => addRule(categoryIndex)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'var(--accent-coral)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ➕ Regel hinzufügen
                  </button>
                </div>

                {category.rules?.map((rule, ruleIndex) => (
                  <div
                    key={ruleIndex}
                    style={{
                      border: '1px solid var(--medium-gray)',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '12px',
                      backgroundColor: 'var(--light-gray)'
                    }}
                  >
                    {/* Rule Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h5>Regel {ruleIndex + 1}</h5>
                      <button
                        onClick={() => removeRule(categoryIndex, ruleIndex)}
                        style={{
                          backgroundColor: '#ff6b6b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>

                    {/* Conditions */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <strong>Bedingungen:</strong>
                        <button
                          onClick={() => addCondition(categoryIndex, ruleIndex)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: 'var(--primary-coral)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          + Bedingung
                        </button>
                      </div>

                      {rule.conditions?.map((condition, conditionIndex) => (
                        <div
                          key={conditionIndex}
                          style={{
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '8px',
                            marginBottom: '8px',
                            backgroundColor: 'white',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 2fr auto',
                            gap: '8px',
                            alignItems: 'center'
                          }}
                        >
                          {/* Field */}
                          <select
                            value={condition.field}
                            onChange={(e) => updateCondition(categoryIndex, ruleIndex, conditionIndex, 'field', e.target.value)}
                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                          >
                            <option value="">Feld wählen...</option>
                            {getAllAvailableFields().map(field => (
                              <option key={field} value={field}>{field}</option>
                            ))}
                          </select>

                          {/* Type */}
                          <select
                            value={condition.type}
                            onChange={(e) => updateCondition(categoryIndex, ruleIndex, conditionIndex, 'type', e.target.value)}
                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                          >
                            {getAllAvailableConditionTypes().map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>

                          {/* Value */}
                          <div>
                            {getValueInput(condition, categoryIndex, ruleIndex, conditionIndex)}
                          </div>

                          {/* Remove Button */}
                          <button
                            onClick={() => removeCondition(categoryIndex, ruleIndex, conditionIndex)}
                            style={{
                              backgroundColor: '#ff6b6b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Content */}
                    <div style={{
                      border: '1px solid var(--accent-coral)',
                      borderRadius: '6px',
                      padding: '12px',
                      backgroundColor: 'white'
                    }}>
                      <strong style={{ marginBottom: '8px', display: 'block' }}>Inhalt:</strong>
                      
                      <div style={{ display: 'grid', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>
                            Kooperationspartner:
                          </label>
                          <select
                            value={rule.content?.["cooperation-partner"] || ""}
                            onChange={(e) => updateContent(categoryIndex, ruleIndex, "cooperation-partner", e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px',
                              borderRadius: '4px',
                              border: '1px solid #ccc'
                            }}
                          >
                            <option value="">Partner wählen...</option>
                            {cooperationPartners.map(partner => (
                              <option key={partner} value={partner}>{partner}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>
                            Link:
                          </label>
                          <input
                            type="text"
                            value={rule.content?.link || ""}
                            onChange={(e) => updateContent(categoryIndex, ruleIndex, "link", e.target.value)}
                            placeholder="https://..."
                            style={{
                              width: '100%',
                              padding: '6px',
                              borderRadius: '4px',
                              border: '1px solid #ccc'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>
                            Text:
                          </label>
                          <textarea
                            value={rule.content?.text || ""}
                            onChange={(e) => updateContent(categoryIndex, ruleIndex, "text", e.target.value)}
                            placeholder="Beschreibungstext..."
                            style={{
                              width: '100%',
                              height: '80px',
                              padding: '6px',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>
                            Bonus-Tipp:
                          </label>
                          <input
                            type="text"
                            value={rule.content?.bonustip || ""}
                            onChange={(e) => updateContent(categoryIndex, ruleIndex, "bonustip", e.target.value)}
                            placeholder="https://..."
                            style={{
                              width: '100%',
                              padding: '6px',
                              borderRadius: '4px',
                              border: '1px solid #ccc'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Move button to bottom */}
        {getAvailableCategories().length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={addCategory}
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--secondary-pink)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginRight: '8px'
              }}
            >
              ➕ Kategorie hinzufügen
            </button>
            <button
              onClick={addCategoryWithRule}
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--primary-coral)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              ➕ Kategorie mit Regel hinzufügen
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          color: 'var(--primary-coral)',
          backgroundColor: 'var(--secondary-pink-light)',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--medium-gray)',
            color: 'var(--text-dark)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          {isProtectedTaxonomy ? 'Schließen' : 'Abbrechen'}
        </button>
        {!isProtectedTaxonomy && (
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: loading ? '#ccc' : 'var(--primary-coral)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '💾 Speichere...' : '💾 Speichern'}
          </button>
        )}
      </div>

      {/* Advanced Mode Toggle */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => setShowAdvancedMode(!showAdvancedMode)}
          style={{
            padding: '8px 16px',
            backgroundColor: showAdvancedMode ? 'var(--primary-coral)' : 'var(--secondary-pink)',
            color: showAdvancedMode ? 'white' : 'var(--text-dark)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          {showAdvancedMode ? ' Hide Advanced Mode' : ' Show Advanced Mode'}
        </button>

        {showAdvancedMode && (
          <div style={{
            border: '2px solid var(--accent-coral)',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'var(--secondary-pink-light)',
            marginBottom: '24px'
          }}>
            <h3 style={{ marginBottom: '16px', color: 'var(--primary-coral)' }}>
              🔧 Advanced JSON Editor
            </h3>

            {/* Natural Language Editor */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                🪄 Natural Language Instructions (Create Partners, Categories or Conditions):
              </label>
              <textarea
                value={naturalLanguagePrompt}
                onChange={(e) => setNaturalLanguagePrompt(e.target.value)}
                placeholder="Example: Add a condition for 'patientDement' equals true under the 'Demenz' category..."
                style={{
                  width: '100%',
                  height: '80px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  resize: 'vertical',
                  marginBottom: '8px'
                }}
              />
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <button
                  onClick={handleNaturalLanguageEdit}
                  disabled={isProcessingNaturalLanguage}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: isProcessingNaturalLanguage ? '#ccc' : 'var(--primary-coral)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isProcessingNaturalLanguage ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {isProcessingNaturalLanguage ? ' Processing...' : '✨ Apply Natural Language'}
                </button>
                <button
                  onClick={handleFallbackToUC001}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                   Fallback to UC001
                </button>
              </div>
            </div>

            {/* JSON Editor */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                📝 JSON Editor:
              </label>
              <textarea
                value={jsonEditor}
                onChange={(e) => handleJsonEditorChange(e.target.value)}
                style={{
                  width: '100%',
                  height: '300px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  marginBottom: '8px'
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleJsonEditorSave}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--primary-coral)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                   Apply JSON Changes
                </button>
                <button
                  onClick={() => setJsonEditor(JSON.stringify(formData, null, 2))}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--secondary-pink)',
                    color: 'var(--text-dark)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  🔄 Reset to Current
                </button>
              </div>
            </div>

            {/* Validation Error Display */}
            {jsonValidationError && (
              <div style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#fee',
                color: '#c33',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                ❌ {jsonValidationError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxonomyEditor; 